/**
 * LineupTab — the XI review surface for a confirmed lineup sheet scan
 * (lineup OCR v2, docs/lineup-scan-contract.md).
 *
 * Tank REVIEWS — scanning and confirming happen in the app. This tab renders
 * the evidence document behind the match's source="sheet" affiliations:
 *
 *  - confirmed header (who / when / the same counts the XI chip summarizes),
 *  - the sheet photo, served authenticated via GET /lineups/{match_id}/{file}
 *    (session cookie in Tank; click to enlarge),
 *  - every parsed row with its verdict, in home/away/attention/ignored groups.
 *    Glyph grammar: matched = green, attention (renumbered / ambiguous /
 *    low-confidence) = amber, unmatched-no-hit = red, staff + chrome = grey.
 *  - per unmatched row a Resolve action (search-first person flow, creation
 *    behind a name check — ResolveRowModal) wired to
 *    POST /api/matches/{id}/lineup-scan/resolve-row/,
 *  - Retract (DELETE) behind a confirm dialog for can_confirm_lineup holders;
 *    a 403 (permission revoked via override) surfaces as a plain message.
 *
 * Jersey numbers from the sheet are VERIFICATION data, never truth (contract
 * invariant 6): the amber "sheet #N" chip on a renumbered row only offers an
 * explicit "Apply #N" editor action (existing PATCH /api/persons/{id}/),
 * gated can_manage_persons.
 */

import { useCallback, useEffect, useState } from 'react';
import clsx from 'clsx';
import { addTeamMember, deleteLineupScan, fetchLineupScan, updatePerson } from '../../../../api';
import { resolveUrl } from '../../../../utils/url';
import { getRelativeTime } from '../../utils/dateUtils';
import { Btn, StatusPill } from '../../../../shared/components/redesign';
import ResolveRowModal from './ResolveRowModal';
import styles from './LineupTab.module.css';

const UNMATCHED_VERDICTS = new Set([
  'unmatched-no-hit',
  'unmatched-ambiguous',
  'unmatched-low-confidence',
]);

/**
 * Verdict → glyph color, following the app's amber/green/red glyph grammar
 * (matrix-app src/utils/liveGlyph.ts): green = resolved, amber = needs a
 * look, red = no hit at all; staff and sheet chrome are grey (not the XI).
 */
export function verdictGlyph(verdict) {
  switch (verdict) {
    case 'matched':
      return 'green';
    case 'matched-renumbered':
    case 'unmatched-ambiguous':
    case 'unmatched-low-confidence':
      return 'amber';
    case 'unmatched-no-hit':
      return 'red';
    default:
      // staff / ignored-chrome / anything a future schema adds
      return 'grey';
  }
}

function verdictLabel(verdict) {
  switch (verdict) {
    case 'matched':
      return 'Matched';
    case 'matched-renumbered':
      return 'Renumbered';
    case 'unmatched-no-hit':
      return 'No match';
    case 'unmatched-ambiguous':
      return 'Ambiguous';
    case 'unmatched-low-confidence':
      return 'Low confidence';
    case 'staff':
      return 'Staff';
    case 'ignored-chrome':
      return 'Sheet chrome';
    default:
      return verdict || '—';
  }
}

function GlyphDot({ glyph }) {
  return (
    <span
      className={clsx(styles.glyph, styles[`glyph_${glyph}`])}
      aria-hidden="true"
    />
  );
}

/**
 * One parsed sheet row. `row.match` is present on the two matched verdicts
 * (and on rows resolve-row synthesized a match for); otherwise render the
 * parsed line as-is.
 */
function SheetRow({
  row,
  rowIndex,
  canConfirmLineup,
  canManagePersons,
  onResolve,
  onApplyJersey,
  applyingJersey,
}) {
  const glyph = verdictGlyph(row.verdict);
  const match = row.match || null;
  const parsed = row.parsed || {};
  const name = match?.display_name || parsed.name || parsed.raw || '—';
  const jersey = match?.jersey_on_sheet ?? parsed.jersey ?? null;
  const renumbered = row.verdict === 'matched-renumbered';
  // Matched, the sheet has a number, the roster has NONE: the sync never
  // writes it (invariant 6), so without an explicit apply the number stays
  // empty forever. Same Apply #N, muted chip instead of the amber one.
  const rosterMissing =
    row.verdict === 'matched' && jersey != null && match?.jersey_on_roster == null;
  const unmatched = UNMATCHED_VERDICTS.has(row.verdict);
  // Unlinked staff rows resolve too (coach or referee from the desk, parity
  // with the app since 03-09); a linked one carries `match` and is done.
  const linkable = unmatched || (row.verdict === 'staff' && !match);

  return (
    <li className={styles.row}>
      <GlyphDot glyph={glyph} />
      <span className={styles.rowJersey}>{jersey != null ? `#${jersey}` : '·'}</span>
      <span className={clsx(styles.rowName, glyph === 'grey' && styles.rowNameMuted)}>
        {name}
      </span>
      {renumbered && (
        <span className={styles.renumberChip} title="Jersey on the sheet differs from the roster — verification data, never written automatically">
          roster #{match?.jersey_on_roster ?? '?'}
        </span>
      )}
      {rosterMissing && (
        <span className={styles.rosterMissingChip} title="The roster has no number for this player; the sheet's number is verification data until applied">
          no roster #
        </span>
      )}
      {(renumbered || rosterMissing) && canManagePersons && match?.person_id && jersey != null && (
        <button
          type="button"
          className={styles.applyJerseyBtn}
          disabled={applyingJersey}
          onClick={() => onApplyJersey(match.person_id, jersey, match.side)}
          title="Explicitly write the sheet's number to this player"
        >
          Apply #{jersey}
        </button>
      )}
      <span className={clsx(styles.rowVerdict, styles[`verdict_${glyph}`])}>
        {verdictLabel(row.verdict)}
      </span>
      {linkable && canConfirmLineup && (
        <button
          type="button"
          className={styles.resolveBtn}
          onClick={() => onResolve(rowIndex)}
        >
          Resolve
        </button>
      )}
    </li>
  );
}

function RowSection({ title, children }) {
  return (
    <div className={styles.section}>
      <span className={styles.sectionEyebrow}>{title}</span>
      <ul className={styles.rowList}>{children}</ul>
    </div>
  );
}

export default function LineupTab({
  match,
  canConfirmLineup = false,
  canManagePersons = false,
  requestConfirm,
  onChanged,
}) {
  const [scan, setScan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [resolvingIndex, setResolvingIndex] = useState(null);
  const [sheetEnlarged, setSheetEnlarged] = useState(false);
  const [applyingJersey, setApplyingJersey] = useState(false);
  const [applied, setApplied] = useState(null);

  const matchId = match?.id;

  const load = useCallback(async () => {
    if (!matchId) return;
    setLoading(true);
    setError(null);
    try {
      const data = await fetchLineupScan(matchId);
      setScan(data);
    } catch (err) {
      // 404 is the documented empty state, not an error.
      if (err.response?.status === 404) {
        setScan(null);
      } else {
        console.error('Lineup scan load failed:', err);
        setError('Could not load the lineup scan.');
      }
    } finally {
      setLoading(false);
    }
  }, [matchId]);

  useEffect(() => {
    load();
  }, [load]);

  const handleRetract = () => {
    requestConfirm({
      title: 'Retract lineup scan?',
      message:
        'This removes the confirmed sheet scan, its photo and every affiliation ' +
        'the sheet produced. Manual and MatchHub affiliations are untouched. ' +
        'The XI chip returns to neutral for everyone.',
      confirmLabel: 'Retract',
      onConfirm: async () => {
        try {
          await deleteLineupScan(matchId);
          setScan(null);
          onChanged?.();
        } catch (err) {
          if (err.response?.status === 403) {
            setError('You don’t have permission to retract this scan.');
          } else {
            console.error('Lineup scan retract failed:', err);
            setError('Could not retract the lineup scan.');
          }
        }
      },
    });
  };

  // Explicit editor action per contract invariant 6 — the ONLY path from a
  // sheet number to a Person record. Two fields carry a number: the
  // person-level one (PATCH /api/persons/{id}/, what the XI review reads
  // back) and the club MEMBERSHIP one (what the scan roster and the app's
  // Players chips read). Both are written; add_member is idempotent.
  const writeJersey = async (personId, jersey, side) => {
    await updatePerson(personId, { jersey_number: jersey });
    const teamId = side === 'home' ? match?.home_team : match?.away_team;
    if (teamId) await addTeamMember(teamId, personId, jersey);
  };

  // Reflect writes locally: rows plainly matched, roster carrying the
  // number, chips and buttons gone.
  const reflectApplied = (byPerson) => {
    setScan((prev) => {
      if (!prev?.payload?.rows) return prev;
      const rows = prev.payload.rows.map((r) => {
        const jersey = byPerson.get(r.match?.person_id);
        if (jersey == null) return r;
        return {
          ...r,
          verdict: 'matched',
          match: { ...r.match, jersey_on_roster: jersey, jersey_mismatch: false },
        };
      });
      return { ...prev, payload: { ...prev.payload, rows } };
    });
  };

  const handleApplyJersey = async (personId, jersey, side) => {
    setApplyingJersey(true);
    setError(null);
    setApplied(null);
    try {
      await writeJersey(personId, jersey, side);
      const name =
        scan?.payload?.rows?.find((r) => r.match?.person_id === personId)?.match?.display_name ?? null;
      reflectApplied(new Map([[personId, jersey]]));
      setApplied({ text: `Applied #${jersey}${name ? ` to ${name}` : ''}.` });
      onChanged?.();
    } catch (err) {
      console.error('Jersey apply failed:', err);
      setError('Could not apply the jersey number.');
    } finally {
      setApplyingJersey(false);
    }
  };

  // Every matched row whose roster has NO number: safe in one go, nothing
  // is overwritten. Mismatches stay a per-row decision.
  const emptyRosterRows = (scan?.payload?.rows ?? []).filter(
    (r) =>
      r.verdict === 'matched' &&
      r.match?.person_id &&
      r.match?.jersey_on_sheet != null &&
      r.match?.jersey_on_roster == null
  );

  const handleApplyAllEmpty = async () => {
    if (emptyRosterRows.length === 0) return;
    setApplyingJersey(true);
    setError(null);
    setApplied(null);
    const done = new Map();
    const failed = [];
    for (const r of emptyRosterRows) {
      try {
        await writeJersey(r.match.person_id, r.match.jersey_on_sheet, r.match.side);
        done.set(r.match.person_id, r.match.jersey_on_sheet);
      } catch (err) {
        console.error('Jersey apply failed:', err);
        failed.push(r.match.display_name || r.match.person_id);
      }
    }
    reflectApplied(done);
    setApplied({
      text:
        `Applied ${done.size} sheet number${done.size === 1 ? '' : 's'} to empty roster spots.` +
        (failed.length ? ` Failed: ${failed.join(', ')}.` : ''),
    });
    if (failed.length) setError('Some jersey numbers could not be applied.');
    onChanged?.();
    setApplyingJersey(false);
  };

  const handleResolved = (updatedScan) => {
    // Name the outcome and where the row went: it re-renders in its new
    // bucket behind this note (an official leaves Needs attention for
    // Staff & sheet chrome, a player joins a side).
    const row = updatedScan?.payload?.rows?.[resolvingIndex];
    const who = row?.match?.display_name || 'Person';
    const side = row?.match?.side;
    if (side === 'home' || side === 'away') {
      const team = (side === 'home' ? match?.home_team_name : match?.away_team_name) || side;
      setApplied({ text: `Linked ${who} to ${team}.` });
    } else if (row?.verdict === 'staff') {
      setApplied({
        text: `Linked ${who} as ${row?.parsed?.role_hint || 'official'} without a team. The line now sits under Staff & sheet chrome.`,
      });
    } else {
      setApplied(null);
    }
    setError(null);
    setResolvingIndex(null);
    setScan(updatedScan);
    onChanged?.();
  };

  if (loading) {
    return <p className={styles.emptyNote}>Loading lineup scan…</p>;
  }

  if (!scan) {
    return (
      <div className={styles.empty}>
        {error && <p className={styles.error}>{error}</p>}
        <p className={styles.emptyNote}>No confirmed lineup scan for this match.</p>
        <p className={styles.emptyHint}>
          Scan the physical team sheet in the app — a confirmed scan turns the
          XI chip green everywhere and shows up here for review.
        </p>
      </div>
    );
  }

  const rows = Array.isArray(scan.payload?.rows) ? scan.payload.rows : [];
  const indexed = rows.map((row, index) => ({ row, index }));
  const isMatched = ({ row }) =>
    row?.match && !UNMATCHED_VERDICTS.has(row.verdict) && row.verdict !== 'staff';
  const homeRows = indexed.filter((r) => isMatched(r) && r.row.match.side === 'home');
  const awayRows = indexed.filter((r) => isMatched(r) && r.row.match.side !== 'home');
  const unmatchedRows = indexed.filter(({ row }) => UNMATCHED_VERDICTS.has(row?.verdict));
  const ignoredRows = indexed.filter(
    ({ row }) => row?.verdict === 'staff' || row?.verdict === 'ignored-chrome'
  );

  const sheetSrc = resolveUrl(scan.sheet_url);
  const resolvingRow = resolvingIndex != null ? rows[resolvingIndex] : null;

  return (
    <div className={styles.container}>
      {error && <p className={styles.error}>{error}</p>}
      {applied && (
        <p className={styles.appliedNote}>{applied.text}</p>
      )}

      {/* Confirmed header — green XI state; same numbers as the chip summary. */}
      <div className={styles.section}>
        <div className={styles.confirmedHeader}>
          <StatusPill kind="done" compact>
            XI confirmed
          </StatusPill>
          <span className={styles.confirmedBy}>
            by {scan.confirmed_by_name || 'unknown'}
            {scan.confirmed_at ? ` · ${getRelativeTime(scan.confirmed_at)}` : ''}
          </span>
          {canManagePersons && emptyRosterRows.length > 0 && (
            <Btn
              kind="soft"
              size="sm"
              disabled={applyingJersey}
              onClick={handleApplyAllEmpty}
              title="Write every sheet number whose roster spot is still empty. Nothing is overwritten."
            >
              {`Apply ${emptyRosterRows.length} sheet number${emptyRosterRows.length === 1 ? '' : 's'}`}
            </Btn>
          )}
          {canConfirmLineup && (
            <Btn kind="danger" size="sm" onClick={handleRetract}>
              Retract
            </Btn>
          )}
        </div>
        <div className={styles.countsRow}>
          <span className={clsx(styles.count, styles.verdict_green)}>
            {scan.matched_count} matched
          </span>
          <span className={clsx(styles.count, styles.verdict_amber)}>
            {scan.attention_count} attention
          </span>
          <span className={clsx(styles.count, styles.verdict_red)}>
            {scan.unmatched_count} unmatched
          </span>
          <span className={clsx(styles.count, styles.verdict_grey)}>
            {scan.staff_count} staff
          </span>
        </div>
      </div>

      {/* The sheet photo — the 2000px downscaled evidence image. */}
      <div className={styles.section}>
        <span className={styles.sectionEyebrow}>Team sheet</span>
        <button
          type="button"
          className={styles.sheetButton}
          onClick={() => setSheetEnlarged(true)}
          title="Click to enlarge"
        >
          <img className={styles.sheetImage} src={sheetSrc} alt="Scanned team sheet" />
        </button>
      </div>

      {homeRows.length > 0 && (
        <RowSection title={`Home · ${match.home_team_name || 'Home'}`}>
          {homeRows.map(({ row, index }) => (
            <SheetRow
              key={index}
              row={row}
              rowIndex={index}
              canConfirmLineup={canConfirmLineup}
              canManagePersons={canManagePersons}
              onResolve={setResolvingIndex}
              onApplyJersey={handleApplyJersey}
              applyingJersey={applyingJersey}
            />
          ))}
        </RowSection>
      )}

      {awayRows.length > 0 && (
        <RowSection title={`Away · ${match.away_team_name || 'Away'}`}>
          {awayRows.map(({ row, index }) => (
            <SheetRow
              key={index}
              row={row}
              rowIndex={index}
              canConfirmLineup={canConfirmLineup}
              canManagePersons={canManagePersons}
              onResolve={setResolvingIndex}
              onApplyJersey={handleApplyJersey}
              applyingJersey={applyingJersey}
            />
          ))}
        </RowSection>
      )}

      {unmatchedRows.length > 0 && (
        <RowSection title="Needs attention">
          {unmatchedRows.map(({ row, index }) => (
            <SheetRow
              key={index}
              row={row}
              rowIndex={index}
              canConfirmLineup={canConfirmLineup}
              canManagePersons={canManagePersons}
              onResolve={setResolvingIndex}
              onApplyJersey={handleApplyJersey}
              applyingJersey={applyingJersey}
            />
          ))}
        </RowSection>
      )}

      {ignoredRows.length > 0 && (
        <RowSection title="Staff & sheet chrome">
          {ignoredRows.map(({ row, index }) => (
            <SheetRow
              key={index}
              row={row}
              rowIndex={index}
              canConfirmLineup={canConfirmLineup}
              canManagePersons={false}
              onResolve={setResolvingIndex}
              onApplyJersey={() => {}}
              applyingJersey={false}
            />
          ))}
        </RowSection>
      )}

      {sheetEnlarged && (
        <div
          className={styles.lightbox}
          role="dialog"
          aria-label="Team sheet"
          onClick={() => setSheetEnlarged(false)}
        >
          <img className={styles.lightboxImage} src={sheetSrc} alt="Scanned team sheet" />
        </div>
      )}

      {resolvingRow && (
        <ResolveRowModal
          match={match}
          row={resolvingRow}
          rowIndex={resolvingIndex}
          onResolved={handleResolved}
          onClose={() => setResolvingIndex(null)}
        />
      )}
    </div>
  );
}
